import { Component, EventEmitter, Input, OnDestroy, OnInit, Output, QueryList, ViewChildren } from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { MatOption } from '@angular/material/core';
import { TranslateService } from '@ngx-translate/core';
import { AccountNameUid } from '@wb/core/interfaces/account-name-uid';
import { DynamicDateRange } from '@wb/core/interfaces/dynamic-date-range';
import { AuthService } from '@wb/core/services/auth.service';
import dayjs from 'dayjs';
import quarterOfYear from 'dayjs/plugin/quarterOfYear';
import { Subject } from 'rxjs';
import { pairwise, startWith, takeUntil } from 'rxjs/operators';
import { ModeWidgetParameters, UserWidgetParameters } from '../../../core/interfaces/widget';
import { WidgetService } from '../../../core/services/widget.service';

dayjs.extend(quarterOfYear);

enum BeginEnd {
  BEGIN,
  END,
}

@Component({
  selector: 'app-graph-embed-filter',
  templateUrl: './graph-embed-filter.component.html',
  styleUrls: ['./graph-embed-filter.component.sass'],
})
export class GraphEmbedFilterComponent implements OnDestroy, OnInit {
  @Input() widgetUserUid?: string;

  @Input() widgetParameters?: ModeWidgetParameters;

  // eslint-disable-next-line @angular-eslint/no-output-on-prefix
  @Output() onSave: EventEmitter<void> = new EventEmitter();

  public loading: boolean = true;

  @ViewChildren(MatOption) options?: QueryList<MatOption>;

  public accounts: AccountNameUid[] = [];

  private destroyed$: Subject<void> = new Subject<void>();

  public dynamicDateRanges: DynamicDateRange[] = this.widgetService.getDynamicDateRanges();

  public dynamicDateRangeIndex = -1;

  public allAccountsSelected = true;

  formGroup: FormGroup = new FormGroup({
    sessionAccountUid: new FormControl({ disabled: true }, []),
    type: new FormControl(null, []),
    dynamicDate: new FormControl(null, []),
    beginDate: new FormControl(dayjs().startOf('month').format('YYYY-MM-DD'), []),
    endDate: new FormControl(dayjs().format('YYYY-MM-DD'), []),
  });

  constructor(private translate: TranslateService, private widgetService: WidgetService, private auth: AuthService) {}

  async ngOnInit() {
    this.widgetService.userWidgetAccounts$.pipe(takeUntil(this.destroyed$)).subscribe({
      next: (accounts) => {
        this.accounts = accounts.sort((a, b) => (a.name <= b.name ? -1 : 1));
      },
    });

    this.widgetService.widgetsLoading$.pipe(takeUntil(this.destroyed$)).subscribe({
      next: (loading) => {
        this.loading = loading;
      },
    });

    // Ensures that the accounts dropdown can never have nothing selected
    this.formGroup
      .get('sessionAccountUid')
      ?.valueChanges.pipe(startWith(null), pairwise(), takeUntil(this.destroyed$))
      .subscribe(([prev, next]: [string[], string[]]) => {
        let updateValue: string[] = next;
        if (prev !== next && prev && this.accounts.length > 1) {
          // Unselect all when any account is selected
          if (prev[0] === 'all' || prev.length === 0) {
            updateValue.shift();
          }
          // Unselect accounts when 'all' is selected and select all when there are no accounts selected
          if (next[0] === 'all' || next.length === 0) {
            updateValue = ['all'];
          }
        } else if (this.accounts.length === 1) {
          // If there is only one internal account available, autoselect it
          updateValue = [this.accounts[0].uid];
        }
        this.formGroup.patchValue({ sessionAccountUid: updateValue }, { emitEvent: false, onlySelf: true });
      });

    this.dynamicDateRangeIndex = this.widgetParameters?.param_session_dynamic_date_range_key ?? -1;

    this.formGroup.setValue({
      sessionAccountUid: this.widgetParameters?.param_session_account_uid ?? ['all'],
      type: this.widgetParameters?.param_item_type ?? ['all'],
      dynamicDate:
        this.dynamicDateRangeIndex >= 0
          ? this.dynamicDateRanges[this.dynamicDateRangeIndex]
          : this.dynamicDateRanges[5],
      beginDate: this.widgetParameters?.param_session_begin_date ?? dayjs().startOf('month').format('YYYY-MM-DD'),
      endDate: this.widgetParameters?.param_session_end_date ?? dayjs().format('YYYY-MM-DD'),
    });

    // default dynamicDateRangeIndex to 'thisMonth' if previously unset
    this.dynamicDateRangeIndex = this.dynamicDateRangeIndex < 0 ? 5 : this.dynamicDateRangeIndex;
  }

  selectedAccountsChanged(isFirst: boolean = false) {
    const selections: any[] = this.formGroup.value.sessionAccountUid;

    if (!this.options) {
      return;
    }

    if (selections && selections.length > 0) {
      const matSelects = this.options.filter((result) => selections.indexOf(result.value) > -1);

      if (isFirst && matSelects.length > 1) {
        for (let i = 1; i < matSelects.length; i++) {
          matSelects[i].deselect();
        }
      } else {
        this.options.first.deselect();
      }
    } else {
      this.options.first.select();
    }
  }

  applyFilters() {
    if (this.widgetUserUid) {
      this.onUpdateWidgetParameters(this.widgetUserUid, {
        sessionAccountUid: this.formGroup.get('sessionAccountUid')?.value,
        itemType: this.formGroup.get('type')?.value,
        sessionDynamicDateRange: this.translate.instant(this.formGroup.get('dynamicDate')?.value.title),
        sessionDynamicDateRangeKey: this.formGroup.get('dynamicDate')?.value.key,
        sessionBeginDate: this.formGroup.get('beginDate')?.value,
        sessionEndDate: this.formGroup.get('endDate')?.value,
      });
      this.onSave.emit();
    }
  }

  ngOnDestroy() {
    this.destroyed$.next();
    this.destroyed$.complete();
  }

  dynamicDateChanged() {
    const select = this.formGroup.controls.dynamicDate;
    this.dynamicDateRangeIndex = this.dynamicDateRanges.findIndex(
      (dynamicDateRange) => dynamicDateRange.key === select.value.key,
    );
  }

  getDynamicDate(beginEnd: BeginEnd) {
    return beginEnd === BeginEnd.BEGIN
      ? dayjs(this.dynamicDateRanges[this.dynamicDateRangeIndex].beginDate).format('MM/DD/YYYY')
      : dayjs(this.dynamicDateRanges[this.dynamicDateRangeIndex].endDate).format('MM/DD/YYYY');
  }

  private onUpdateWidgetParameters(widgetUid: string, parameters: UserWidgetParameters) {
    this.widgetService.updateUserWidgetParameters(widgetUid, parameters);
  }
}
